<template>
  <div class="dashboard">
    <div class="page-header">
      <div>
        <h2 class="page-title">数据看板</h2>
        <p class="page-desc">欢迎回来，{{ userName }}！以下是您的店铺运营数据</p>
      </div>
      <div class="header-actions">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          size="default"
          @change="fetchAllData"
        />
        <el-button type="primary" @click="refreshData" :loading="refreshing">
          <el-icon><Refresh /></el-icon> 刷新
        </el-button>
      </div>
    </div>
    
    <!-- 统计卡片 -->
    <div class="stat-grid">
      <div class="stat-card" v-for="stat in stats" :key="stat.label" @click="stat.path && goTo(stat.path)">
        <div class="stat-icon" :style="{ background: stat.bgColor }">
          <component :is="stat.icon" :size="28" :color="stat.color" />
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ stat.value }}</div>
          <div class="stat-label">{{ stat.label }}</div>
          <div class="stat-trend" v-if="stat.trend !== null">
            <span :class="stat.trend > 0 ? 'trend-up' : stat.trend < 0 ? 'trend-down' : 'trend-neutral'">
              {{ stat.trend > 0 ? '+' : '' }}{{ stat.trend }}%
            </span>
            <span>较昨日</span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 图表区域 -->
    <div class="charts-section">
      <el-row :gutter="20">
        <el-col :xs="24" :lg="12">
          <div class="chart-card">
            <div class="chart-header">
              <h3>近7日订单趋势</h3>
              <el-tag type="primary" size="small">订单数</el-tag>
            </div>
            <div ref="orderChartRef" style="height: 300px;"></div>
            <div v-if="!hasOrderData && orderTrendData.dates && orderTrendData.dates.length > 0" class="chart-empty-overlay">
              <el-empty description="暂无订单数据" :image-size="80" />
            </div>
          </div>
        </el-col>
        <el-col :xs="24" :lg="12">
          <div class="chart-card">
            <div class="chart-header">
              <h3>商品销售排行</h3>
              <el-tag type="success" size="small">销量TOP5</el-tag>
            </div>
            <div ref="productChartRef" style="height: 300px;"></div>
            <div v-if="!hasProductData && productRankingData.names && productRankingData.names.length > 0" class="chart-empty-overlay">
              <el-empty description="暂无销售数据" :image-size="80" />
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
    
    <!-- 数据表格区域 -->
    <div class="tables-section">
      <el-row :gutter="20">
        <el-col :xs="24" :lg="12">
          <div class="data-card">
            <div class="card-header">
              <h3>最新订单</h3>
              <el-button type="primary" link @click="goTo('/order')">查看更多</el-button>
            </div>
            <el-table :data="recentOrders" size="small" v-loading="ordersLoading" max-height="300">
              <el-table-column prop="order_no" label="订单号" width="140" />
              <el-table-column prop="product_name" label="商品" min-width="120" show-overflow-tooltip />
              <el-table-column label="金额" width="80">
                <template #default="{ row }">¥{{ row.pay_amount }}</template>
              </el-table-column>
              <el-table-column label="状态" width="80">
                <template #default="{ row }">
                  <el-tag :type="getOrderStatusType(row.order_status)" size="small">
                    {{ getOrderStatusText(row.order_status) }}
                  </el-tag>
                </template>
              </el-table-column>
              <template #empty>
                <div class="table-empty">暂无订单数据</div>
              </template>
            </el-table>
          </div>
        </el-col>
        <el-col :xs="24" :lg="12">
          <div class="data-card">
            <div class="card-header">
              <h3>热门商品</h3>
              <el-button type="primary" link @click="goTo('/product/list')">查看更多</el-button>
            </div>
            <el-table :data="hotProducts" size="small" v-loading="productsLoading" max-height="300">
              <el-table-column label="商品" min-width="180" show-overflow-tooltip>
                <template #default="{ row }">{{ row.product_name }}</template>
              </el-table-column>
              <el-table-column label="销量" width="80" align="center">
                <template #default="{ row }">{{ row.sold_count || 0 }}</template>
              </el-table-column>
              <el-table-column label="售价" width="80">
                <template #default="{ row }">¥{{ row.sale_price }}</template>
              </el-table-column>
              <template #empty>
                <div class="table-empty">暂无商品数据</div>
              </template>
            </el-table>
          </div>
        </el-col>
      </el-row>
    </div>
    
    <!-- 底部统计 -->
    <div class="footer-stats">
      <div class="footer-stat">
        <div class="footer-value">{{ statsData.orderCompleteRate || '0' }}%</div>
        <div class="footer-label">订单完成率</div>
      </div>
      <div class="footer-stat">
        <div class="footer-value">¥{{ formatMoney(statsData.totalRevenue) }}</div>
        <div class="footer-label">累计营业额</div>
      </div>
      <div class="footer-stat">
        <div class="footer-value">{{ statsData.avgOrderAmount || '0' }}</div>
        <div class="footer-label">客单价(元)</div>
      </div>
      <div class="footer-stat">
        <div class="footer-value">{{ statsData.activeRate || '0' }}%</div>
        <div class="footer-label">会员活跃率</div>
      </div>
    </div>
    
    <!-- 引导创建数据的提示（当所有数据都为空时显示） -->
    <div v-if="isAllEmpty" class="guide-card">
      <el-empty description="暂无数据，开始创建您的第一个数据吧" :image-size="160">
        <div class="guide-buttons">
          <el-button type="primary" @click="goTo('/store')">
            <el-icon><Shop /></el-icon> 添加门店
          </el-button>
          <el-button @click="goTo('/product/add')">
            <el-icon><Goods /></el-icon> 添加商品
          </el-button>
          <el-button @click="goTo('/member/list')">
            <el-icon><User /></el-icon> 邀请会员
          </el-button>
        </div>
      </el-empty>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import Cookies from 'js-cookie'
import axios from 'axios'
import * as echarts from 'echarts'
import config from '@/config'
import { Shop, Goods, Document, User, Present, Setting, Refresh } from '@element-plus/icons-vue'

export default {
  name: 'DashboardIndex',
  components: { Shop, Goods, Document, User, Present, Setting, Refresh },
  setup() {
    const router = useRouter()
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const agentId = ref(userInfo.agent_id || 0)
    const storeId = ref(userInfo.id)
    const userName = ref(userInfo.account || userInfo.nickName || '管理员')
    
    const refreshing = ref(false)
    const ordersLoading = ref(false)
    const productsLoading = ref(false)
    const dateRange = ref([])
    
    // DOM 引用
    const orderChartRef = ref(null)
    const productChartRef = ref(null)
    
    // 图表数据存储
    const orderTrendData = ref({ dates: [], counts: [] })
    const productRankingData = ref({ names: [], sales: [] })
    
    const stats = ref([
      { label: '门店数量', value: '0', icon: 'Shop', color: '#409eff', bgColor: '#e8f3ff', trend: null, path: '/store' },
      { label: '商品总数', value: '0', icon: 'Goods', color: '#67c23a', bgColor: '#e6f7e6', trend: null, path: '/product/list' },
      { label: '今日订单', value: '0', icon: 'Document', color: '#e6a23c', bgColor: '#fff0e6', trend: null, path: '/order' },
      { label: '会员总数', value: '0', icon: 'User', color: '#909399', bgColor: '#f4f4f5', trend: null, path: '/member/list' }
    ])
    
    const statsData = ref({
      orderCompleteRate: 0,
      totalRevenue: 0,
      avgOrderAmount: 0,
      activeRate: 0
    })
    
    const recentOrders = ref([])
    const hotProducts = ref([])
    
    // 图表实例
    let orderChart = null
    let productChart = null
    
    // 计算属性：是否有订单数据
    const hasOrderData = computed(() => {
      return orderTrendData.value.counts && orderTrendData.value.counts.some(c => c > 0)
    })
    
    // 计算属性：是否有商品销售数据
    const hasProductData = computed(() => {
      return productRankingData.value.sales && productRankingData.value.sales.some(s => s > 0)
    })
    
    // 计算属性：是否所有数据都为空
    const isAllEmpty = computed(() => {
      const totalStores = parseInt(stats.value[0].value) || 0
      const totalProducts = parseInt(stats.value[1].value) || 0
      const totalMembers = parseInt(stats.value[3].value) || 0
      return totalStores === 0 && totalProducts === 0 && totalMembers === 0
    })
    
    // 获取订单状态
    const getOrderStatusText = (status) => {
      const map = { 0: '待支付', 1: '待使用', 2: '已使用', 3: '已退款', 4: '已过期' }
      return map[status] || '未知'
    }
    
    const getOrderStatusType = (status) => {
      const map = { 0: 'info', 1: 'warning', 2: 'success', 3: 'danger', 4: 'info' }
      return map[status] || 'info'
    }
    
    const formatMoney = (value) => {
      if (!value) return '0'
      if (value >= 10000) {
        return (value / 10000).toFixed(1) + '万'
      }
      return value.toFixed(2)
    }
    
    const goTo = (path) => {
      router.push(path)
    }
    
    // 获取概览数据
    const fetchOverview = async () => {
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/dashboard_api.php`, {
          action: 'get_overview',
          agent_id: agentId.value,
          store_id: storeId.value
        })
        
        if (response.data.code === 200 && response.data.success) {
          const data = response.data.data
          stats.value[0].value = data.totalStores || 0
          stats.value[1].value = data.totalProducts || 0
          stats.value[2].value = data.todayOrders || 0
          stats.value[3].value = data.totalMembers || 0
          
          stats.value[0].trend = data.storeTrend || 0
          stats.value[1].trend = data.productTrend || 0
          stats.value[2].trend = data.orderTrend || 0
          stats.value[3].trend = data.memberTrend || 0
          
          statsData.value.orderCompleteRate = data.orderCompleteRate || 0
          statsData.value.totalRevenue = data.totalRevenue || 0
          statsData.value.avgOrderAmount = data.avgOrderAmount || 0
          statsData.value.activeRate = data.activeRate || 0
        }
      } catch (error) {
        console.error('获取概览数据失败:', error)
      }
    }
    
    // 获取近7日订单趋势
    const fetchOrderTrend = async () => {
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/dashboard_api.php`, {
          action: 'get_order_trend',
          agent_id: agentId.value,
          store_id: storeId.value,
          days: 7
        })
        
        if (response.data.code === 200 && response.data.success) {
          orderTrendData.value = response.data.data
        } else {
          orderTrendData.value = { dates: [], counts: [] }
        }
      } catch (error) {
        console.error('获取订单趋势失败:', error)
        orderTrendData.value = { dates: [], counts: [] }
      }
    }
    
    // 获取商品销售排行
    const fetchProductRanking = async () => {
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/dashboard_api.php`, {
          action: 'get_product_ranking',
          agent_id: agentId.value,
          store_id: storeId.value,
          limit: 5
        })
        
        if (response.data.code === 200 && response.data.success) {
          productRankingData.value = response.data.data
        } else {
          productRankingData.value = { names: [], sales: [] }
        }
      } catch (error) {
        console.error('获取商品排行失败:', error)
        productRankingData.value = { names: [], sales: [] }
      }
    }
    
    // 获取最新订单
    const fetchRecentOrders = async () => {
      ordersLoading.value = true
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/dashboard_api.php`, {
          action: 'get_recent_orders',
          agent_id: agentId.value,
          store_id: storeId.value,
          limit: 5
        })
        
        if (response.data.code === 200 && response.data.success) {
          recentOrders.value = response.data.data
        }
      } catch (error) {
        console.error('获取最新订单失败:', error)
        recentOrders.value = []
      } finally {
        ordersLoading.value = false
      }
    }
    
    // 获取热门商品
    const fetchHotProducts = async () => {
      productsLoading.value = true
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/dashboard_api.php`, {
          action: 'get_hot_products',
          agent_id: agentId.value,
          store_id: storeId.value,
          limit: 5
        })
        
        if (response.data.code === 200 && response.data.success) {
          hotProducts.value = response.data.data
        }
      } catch (error) {
        console.error('获取热门商品失败:', error)
        hotProducts.value = []
      } finally {
        productsLoading.value = false
      }
    }
    
    // 初始化订单趋势图
    const initOrderChart = () => {
      if (!orderChartRef.value) return
      
      if (orderChart) {
        orderChart.dispose()
      }
      
      orderChart = echarts.init(orderChartRef.value)
      
      // 如果没有数据，显示空图表提示
      if (!hasOrderData.value) {
        const option = {
          title: {
            show: true,
            text: '暂无订单数据',
            left: 'center',
            top: 'center',
            textStyle: {
              color: '#999',
              fontSize: 14,
              fontWeight: 'normal'
            }
          },
          xAxis: { show: false },
          yAxis: { show: false },
          series: []
        }
        orderChart.setOption(option)
        return
      }
      
      const dates = orderTrendData.value.dates || []
      const counts = orderTrendData.value.counts || []
      
      const option = {
        tooltip: { trigger: 'axis' },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', boundaryGap: false, data: dates },
        yAxis: { type: 'value', name: '订单数' },
        series: [{
          name: '订单数',
          type: 'line',
          smooth: true,
          lineStyle: { width: 3, color: '#409eff' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
              { offset: 1, color: 'rgba(64, 158, 255, 0.05)' }
            ])
          },
          data: counts,
          connectNulls: true
        }]
      }
      orderChart.setOption(option)
    }
    
    // 初始化商品销售排行图
    const initProductChart = () => {
      if (!productChartRef.value) return
      
      if (productChart) {
        productChart.dispose()
      }
      
      productChart = echarts.init(productChartRef.value)
      
      // 如果没有数据，显示空图表提示
      if (!hasProductData.value) {
        const option = {
          title: {
            show: true,
            text: '暂无销售数据',
            left: 'center',
            top: 'center',
            textStyle: {
              color: '#999',
              fontSize: 14,
              fontWeight: 'normal'
            }
          },
          xAxis: { show: false },
          yAxis: { show: false },
          series: []
        }
        productChart.setOption(option)
        return
      }
      
      const names = productRankingData.value.names || []
      const sales = productRankingData.value.sales || []
      
      const option = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'value', name: '销量' },
        yAxis: { type: 'category', data: names, axisLabel: { rotate: 0, overflow: 'break' } },
        series: [{
          name: '销量',
          type: 'bar',
          barWidth: '60%',
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#67c23a' },
              { offset: 1, color: '#85ce61' }
            ]),
            borderRadius: [0, 4, 4, 0]
          },
          data: sales
        }]
      }
      productChart.setOption(option)
    }
    
    // 刷新所有数据
    const refreshData = async () => {
      refreshing.value = true
      await fetchAllData()
      refreshing.value = false
    }
    
    const fetchAllData = async () => {
      await Promise.all([
        fetchOverview(),
        fetchOrderTrend(),
        fetchProductRanking(),
        fetchRecentOrders(),
        fetchHotProducts()
      ])
      
      // 等待 DOM 更新后初始化图表
      await nextTick()
      initOrderChart()
      initProductChart()
    }
    
    // 监听窗口大小变化
    const handleResize = () => {
      if (orderChart) orderChart.resize()
      if (productChart) productChart.resize()
    }
    
    onMounted(() => {
      fetchAllData()
      window.addEventListener('resize', handleResize)
    })
    
    onUnmounted(() => {
      if (orderChart) orderChart.dispose()
      if (productChart) productChart.dispose()
      window.removeEventListener('resize', handleResize)
    })
    
    return {
      userName,
      stats,
      statsData,
      recentOrders,
      hotProducts,
      refreshing,
      ordersLoading,
      productsLoading,
      dateRange,
      hasOrderData,
      hasProductData,
      isAllEmpty,
      orderTrendData,
      productRankingData,
      orderChartRef,
      productChartRef,
      getOrderStatusText,
      getOrderStatusType,
      formatMoney,
      goTo,
      refreshData
    }
  }
}
</script>

<style scoped lang="scss">
.dashboard {
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    
    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }
    
    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
    
    .header-actions {
      display: flex;
      gap: 12px;
      align-items: center;
    }
  }
  
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
    
    .stat-card {
      background: #fff;
      border-radius: 16px;
      padding: 20px;
      display: flex;
      align-items: center;
      gap: 16px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      transition: all 0.3s;
      cursor: pointer;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
      
      .stat-icon {
        width: 56px;
        height: 56px;
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .stat-content {
        flex: 1;
        
        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #1a1a2e;
          line-height: 1.2;
        }
        
        .stat-label {
          font-size: 13px;
          color: #8c8c8c;
          margin-top: 4px;
        }
        
        .stat-trend {
          font-size: 12px;
          margin-top: 8px;
          
          .trend-up { color: #67c23a; }
          .trend-down { color: #f56c6c; }
          .trend-neutral { color: #909399; }
        }
      }
    }
  }
  
  .charts-section {
    margin-bottom: 24px;
    
    .chart-card {
      background: #fff;
      border-radius: 16px;
      padding: 20px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      margin-bottom: 20px;
      position: relative;
      
      .chart-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        
        h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1a1a2e;
          margin: 0;
        }
      }
      
      .chart-empty-overlay {
        position: absolute;
        top: 60px;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.9);
        border-radius: 12px;
        pointer-events: none;
      }
    }
  }
  
  .tables-section {
    margin-bottom: 24px;
    
    .data-card {
      background: #fff;
      border-radius: 16px;
      padding: 20px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      margin-bottom: 20px;
      
      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        
        h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1a1a2e;
          margin: 0;
        }
      }
      
      .table-empty {
        text-align: center;
        padding: 40px;
        color: #8c8c8c;
        font-size: 14px;
      }
    }
  }
  
  .footer-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
    
    .footer-stat {
      background: #fff;
      border-radius: 16px;
      padding: 20px;
      text-align: center;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      
      .footer-value {
        font-size: 24px;
        font-weight: 700;
        color: #409eff;
        margin-bottom: 8px;
      }
      
      .footer-label {
        font-size: 13px;
        color: #8c8c8c;
      }
    }
  }
  
  .guide-card {
    background: #fff;
    border-radius: 16px;
    padding: 40px;
    text-align: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    
    .guide-buttons {
      display: flex;
      gap: 16px;
      justify-content: center;
      margin-top: 24px;
      flex-wrap: wrap;
    }
  }
}

@media (max-width: 768px) {
  .dashboard {
    .stat-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      
      .stat-card {
        padding: 12px;
        
        .stat-icon {
          width: 40px;
          height: 40px;
          
          .el-icon {
            font-size: 20px !important;
          }
        }
        
        .stat-content .stat-value {
          font-size: 20px;
        }
      }
    }
    
    .footer-stats {
      grid-template-columns: repeat(2, 1fr);
    }
    
    .guide-card .guide-buttons {
      flex-direction: column;
      align-items: center;
    }
  }
}
</style>